
import { Flex, ProviderConsumer as FluentUIThemeConsumer, Loader, } from "@fluentui/react-northstar";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { appState, useSetState } from "../../../AppState";
import { platformService } from '../../services/platform.service';
import { EmptyData } from '../EmptyData';

import { useTranslation } from 'react-i18next';
import EmptySVG from '../../../../svg/empty.svg';
import { TicketApprovalStateString } from '../../../lifecycle/utils/constants';
import { updateApprovalList } from '../../../tikit/ticketHelper';
import { ADMINISTRATORS, ANALYSTS, LIFECYCLE_STATUS_GUID, TASK_STATUS_GUID, TicketApprovalState } from '../../utils/constants';
import { TicketLifecyclePhaseContainer } from './TicketLifecyclePhaseContainer';

interface TicketLifecycleSectionProps {
  ticketData: any;
  ticketLifecycle: TicketLifecycle;
  data: any;
  onReloadPhases: () => void;
}

export const TicketLifecycleSection = (Props: TicketLifecycleSectionProps) => {
  const currentState = appState();
  const setAppState = useSetState();
  const api = new platformService();
  const { ticketData, ticketLifecycle, data, onReloadPhases } = Props;
  const [loading, setLoading] = useState(false);
  const [currentOpenedPhase, setCurrentOpenedPhase] = useState(false);
  const [phases, setPhases] = useState<TicketLifecyclePhase[]>([]);
  const hasScrolledInitialRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToPhase = (targetOrder: number) => {
    const container = scrollContainerRef.current;
    const el = document.getElementById(`phase-accordion-${targetOrder}`);
    if (!container || !el) return;
    const offset = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    container.scrollTo({ top: offset, behavior: 'smooth' });
  };

  const storageKey = `lifecycle_expanded_phases_${ticketData.Id}`;
  const expandedPhasesRef = useRef<Set<number>>(new Set<number>());

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        expandedPhasesRef.current = new Set(JSON.parse(stored));
      }
    } catch { /* ignore parse errors */ }
  }, [storageKey]);

  const persistExpandedPhases = useCallback((expanded: Set<number>) => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify([...expanded]));
    } catch { /* ignore storage errors */ }
  }, [storageKey]);

  const onPhaseToggle = useCallback((order: number, isOpen: boolean) => {
    const expanded = expandedPhasesRef.current;
    if (isOpen) {
      expanded.add(order);
    } else {
      expanded.delete(order);
    }
    persistExpandedPhases(expanded);
  }, [persistExpandedPhases]);

  const { t } = useTranslation();

  const refreshApprovalsList = (approval: Approval, approverId: number, status: TicketApprovalState, UpdatedStatus: TicketApprovalState) => {
    const oldApprovals = phases.find(o => o.IsCurrent).Approvals;
    const updatedApprovals = updateApprovalList(oldApprovals, approverId, approval.Id, status, UpdatedStatus);
    const hasPendingApprovals: boolean = updatedApprovals.some((item: any) => item.Approval.ApprovalState == TicketApprovalStateString.Pending);

    setAppState(prevState => ({
      ...prevState,
      lifecycleApprovals: updatedApprovals,
      hasPendingApprovals: hasPendingApprovals
    }));
    setPhases(pre => {
      pre.forEach(phase => { if (phase.IsCurrent) phase.Approvals = [...updatedApprovals] });
      return pre;
    });
  }

  const fetchPhaseData = async () => {
    const ticketLifecyclePhases = await api.getTicketLifecyclePhases(`?$filter=TicketLifecycleId eq ${ticketLifecycle.Id}`);
    const results = await Promise.all([
      api.getTicketApprovals(ticketData.Id),
      api.getTicketTasks(`?$filter=TicketId eq ${ticketData.Id}&$expand=Status,Assignee,Team,SupportGroup,TaskCollaborators`)
    ]);

    const allApprovals: TicketApprovals[] = results[0];
    allApprovals.forEach(approval => {
      approval.Approval.TicketLifecyclePhase = ticketLifecyclePhases.find(x => x.Id == approval.Approval.TicketLifecyclePhaseId);
      return approval;
    });

    const allTasks: TicketTask[] = results[1];
    allTasks.forEach(task => {
      task.TicketLifecyclePhase = ticketLifecyclePhases.find(x => x.Id == task.TicketLifecyclePhaseId);
      return task;
    });

    const arrangedPhases = ticketLifecyclePhases.sort((a, b) => a.Order - b.Order);
    arrangedPhases.forEach((phase: TicketLifecyclePhase) => {
      phase.Tasks = allTasks.filter(x => x.TicketLifecyclePhaseId == phase.Id && !x.IsPowerAutomateTask);
      phase.Approvals = allApprovals.filter(x => x.Approval.TicketLifecyclePhaseId == phase.Id);
      phase.PowerAutomateTasks = allTasks.filter(x => x.TicketLifecyclePhaseId == phase.Id && x.IsPowerAutomateTask);
    });

    return arrangedPhases;
  };

  const shouldExpectPhaseTransition = (currentPhase: TicketLifecyclePhase | undefined): boolean => {
    if (!currentPhase) return false;

    const terminalTaskGuids = [TASK_STATUS_GUID.COMPLETED, TASK_STATUS_GUID.FAILED, TASK_STATUS_GUID.SKIPPED];
    const allTasksDone = (currentPhase.Tasks ?? []).every(t => terminalTaskGuids.includes(t.Status?.Guid ?? ''));
    const allApprovalsDone = (currentPhase.Approvals ?? []).every(a => a.Approval.ApprovalState !== TicketApprovalState.Pending);

    return allTasksDone && allApprovalsDone;
  };

  const loadData = async (isReload: boolean = false, expectPhaseChange: boolean = false) => {
    try {
      const previousCurrentPhaseId = phases.find(p => p.IsCurrent)?.Id;

      let arrangedPhases = await fetchPhaseData();

      if (isReload && expectPhaseChange && previousCurrentPhaseId !== undefined) {
        const fetchedCurrentPhase = arrangedPhases.find(p => p.IsCurrent);
        // Only poll if the fetched data shows all tasks/approvals are done but phase hasn't advanced yet
        if (fetchedCurrentPhase?.Id === previousCurrentPhaseId && shouldExpectPhaseTransition(fetchedCurrentPhase)) {
          const maxRetries = 3;
          const retryDelay = 1200;
          for (let attempt = 0; attempt < maxRetries; attempt++) {
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            arrangedPhases = await fetchPhaseData();
            const newCurrentPhaseId = arrangedPhases.find(p => p.IsCurrent)?.Id;
            if (newCurrentPhaseId !== previousCurrentPhaseId) break;
          }
        }

        // After the phase has changed, wait briefly and re-fetch to ensure
        // the new phase's tasks/approvals are fully reset/populated by the server
        const newCurrentPhase = arrangedPhases.find(p => p.IsCurrent);
        if (newCurrentPhase && newCurrentPhase.Id !== previousCurrentPhaseId) {
          await new Promise(resolve => setTimeout(resolve, 1200));
          arrangedPhases = await fetchPhaseData();

          // Collapse all phases except the new current phase
          const expanded = expandedPhasesRef.current;
          for (const order of [...expanded]) {
            if (order !== newCurrentPhase.Order) {
              expanded.delete(order);
            }
          }
          persistExpandedPhases(expanded);
        }
      }

      setPhases(arrangedPhases);
      if (isReload) {
        const currentPhaseOrder = arrangedPhases?.find((o: any) => o.IsCurrent)?.Order;
        setAppState(prevState => ({ ...prevState, lifecyclePhaseMenu: (currentPhaseOrder ?? prevState.lifecyclePhaseMenu) }));
        onReloadPhases();
      }

    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  const reloadPhases = async (expectPhaseChange: boolean = false) => {
    await loadData(true, expectPhaseChange);
  }

  useEffect(() => {
    if (ticketLifecycle.Id) {
      setLoading(true);
      loadData();
    }
  }, [ticketLifecycle.Id]);

  useEffect(() => {
    if (!loading && phases.length > 0 && !hasScrolledInitialRef.current) {
      hasScrolledInitialRef.current = true;
      const activePhase = phases.find(x => x.IsCurrent);
      if (activePhase) {
        scrollToPhase(activePhase.Order);
      }
    }
  }, [phases, loading]);

  useEffect(() => {
    if (currentState.lifecyclePhaseMenu == undefined || loading || phases.length === 0) return;

    setCurrentOpenedPhase(prev => !prev);
    const targetOrder = currentState.lifecyclePhaseMenu;
    let innerRafId: number;
    const outerRafId = requestAnimationFrame(() => {
      innerRafId = requestAnimationFrame(() => {
        scrollToPhase(targetOrder);
      });
    });
    return () => {
      cancelAnimationFrame(outerRafId);
      cancelAnimationFrame(innerRafId);
    };
  }, [currentState.lifecyclePhaseMenuToken, loading, phases]);

  const isLifecycleCompleted = (): boolean => {
    if (ticketLifecycle.Status?.Guid === LIFECYCLE_STATUS_GUID.COMPLETED || ticketLifecycle.Status?.Guid === LIFECYCLE_STATUS_GUID.FAILED) {
      return true;
    }
    // Fallback: if the last phase is current and all its items are done, lifecycle is effectively complete
    if (phases.length > 0) {
      const lastPhase = phases[phases.length - 1];
      if (lastPhase.IsCurrent && shouldExpectPhaseTransition(lastPhase)) {
        return true;
      }
    }
    return false;
  };

  const openPhase = (phaseData: TicketLifecyclePhase): boolean => {
    if (isLifecycleCompleted()) {
      return expandedPhasesRef.current.has(phaseData.Order);
    }
    const activePhaseOrder = phases.find(x => x.IsCurrent)?.Order ?? 0;
    // Always open the current phase
    if (phaseData.Order === activePhaseOrder) {
      expandedPhasesRef.current.add(phaseData.Order);
      return true;
    }
    // Restore previously expanded phases
    if (expandedPhasesRef.current.has(phaseData.Order)) {
      return true;
    }
    if (currentState.lifecycleMenuOpen && currentState.lifecyclePhaseMenu != undefined) {
      return currentState.lifecyclePhaseMenu == phaseData.Order;
    }
    return false;
  }

  return (
    <FluentUIThemeConsumer
      render={globalTheme => (
        <>
          {(loading) && (
            <Flex.Item>
              <div className={`pt-5 mt-5`}>
                <Loader />
              </div>
            </Flex.Item>
          )}
          {!loading && phases.length == 0 && (
            <Flex.Item>
              <EmptyData
                headerText={t("ticket-details.ticket-conversation.empty-result")}
                subheaderText=""
                SVGIcon={<EmptySVG width={100} height={100} className="mb-2" />}
              />
            </Flex.Item>
          )}
          <Flex.Item styles={{ flexGrow: 1, height: "50%" }}>
            <div ref={scrollContainerRef} className={`print-hidden overflow-auto h-sm:m-h-1/4 h-md:m-h-2/5 lifecycle-form`}>
              {!loading && (
                <div key={`openPhase-${currentOpenedPhase}`}>
                  {phases.map((phaseData, index) => {
                    return (
                      <div key={phaseData.Id} id={`phase-container-${phaseData.Order}`} className={index == (phases.length - 1) ? 'mb-7' : ''}>
                        <TicketLifecyclePhaseContainer
                          statuses={currentState.taskStatus}
                          analysts={currentState.usersInRole}
                          lifecycleStatus={ticketLifecycle.Status}
                          phaseData={phaseData}
                          ticketData={ticketData}
                          phaseIndex={index}
                          activePhaseIndex={phases.find(x => x.IsCurrent)?.Order ?? 0}
                          openPhaseDefault={openPhase(phaseData)}
                          globalTheme={globalTheme}
                          refreshApprovalsList={refreshApprovalsList}
                          reloadPhases={reloadPhases}
                          enableTaskEdit={(ticketLifecycle.Status.Guid == LIFECYCLE_STATUS_GUID.FAILED || ticketLifecycle.Status.Guid == LIFECYCLE_STATUS_GUID.COMPLETED) ? false : true}
                          enableAppovalEdit={(currentState.userRoles.roles.includes(ADMINISTRATORS) || currentState.userRoles.roles.includes(ANALYSTS))}
                          setLoading={setLoading}
                          onPhaseToggle={onPhaseToggle}
                          scrollToPhase={scrollToPhase}
                        /></div>)
                  })
                  }
                </div>
              )}
            </div>
          </Flex.Item>
        </>
      )} />
  );
};